import { z } from 'zod';

import { apiClient } from '@/lib/api';

import { createApiQueryHook } from './useApiQuery';

// Provider account schema
export const providerAccountSchema = z.object({
  id: z.string(),
  provider: z.string(),
  displayName: z.string(),
  externalAccountId: z.string(),
  scopes: z.array(z.string()),
  tokenExpiresAt: z.iso.datetime().nullable(), // ISO datetime or null if no expiry
  createdAt: z.string(),
  updatedAt: z.string(),
  isShared: z.boolean().optional(),
  role: z.enum(['viewer', 'actor', 'admin']).nullable().optional(),
});

// Response schema
export const providerAccountsResponseSchema = z.object({
  items: z.array(providerAccountSchema),
});

export type ProviderAccount = z.infer<typeof providerAccountSchema>;
export type ProviderAccountsResponse = z.infer<
  typeof providerAccountsResponseSchema
>;

export interface ProviderAccountsQueryParams extends Record<string, unknown> {
  provider?: string;
}

export async function fetchProviderAccounts({
  provider,
}: ProviderAccountsQueryParams): Promise<ProviderAccountsResponse> {
  const queryParams = provider
    ? `?provider=${encodeURIComponent(provider)}`
    : '';

  return apiClient.get<ProviderAccountsResponse>(
    `/api/provider-accounts${queryParams}`,
    providerAccountsResponseSchema
  );
}

/**
 * React Query hook for listing provider accounts
 */
export const useListProviderAccounts = createApiQueryHook<
  ProviderAccountsResponse,
  ProviderAccountsQueryParams
>(
  fetchProviderAccounts,
  // Stable query key factory
  ({ provider }) => ['provider-accounts', provider ?? 'all']
);
